import { CheckCircle2, CloudOff, Compass, FileCheck2, Ruler, ShieldCheck } from "lucide-react";
import { NFR_TARGETS } from "@taskiq/shared";
import { Card } from "@/components/ui/Card";
import { IconList } from "@/components/ui/IconList";
import { IconTile } from "@/components/ui/IconTile";
import { Reveal } from "@/components/marketing/Reveal";
import { SecondScale } from "@/components/marketing/SecondScale";
import { PHASES } from "@/components/marketing/assessment";
import { CtaBand, PageHero, Section, SectionIntro } from "@/components/marketing/Section";

/**
 * How it works - the page for someone who has decided they are interested and
 * now wants to know whether it survives contact with their crews.
 *
 * Structured as the sequence, in order, because the product *is* a sequence and
 * the reader's real question is "what does my technician actually do". Numbered
 * markers are used here and nowhere else on the site: the order carries
 * information (the scan has to precede the acknowledgements, the angle check has
 * to precede the climb), which is the only thing that justifies numbering a list.
 */

const STEPS = [
  {
    id: "area-scan",
    icon: Compass,
    title: "Area scan",
    lead: "The technician sweeps the work area with the phone for 20–30 seconds. No framing, no aiming at anything in particular - a slow pan is the whole instruction.",
    detail: [
      "What comes back is specific and in plain language: “wasp nest in the eave above the ladder position”, not “object detected”. A finding a technician cannot picture is a finding they will not act on.",
      "Each finding carries one of three tiers, and each is acknowledged, noted or escalated before work begins. Nothing is dismissed silently.",
    ],
    targets: [`Hazard list back in under ${NFR_TARGETS.hazardListSeconds} seconds online`],
  },
  {
    // The anchor stays `ladder-angle`, and `nav.ts` says why: it is a real URL
    // that may be linked or bookmarked. The section it opens has grown a second
    // step since it was named; the id is not the place to record that.
    id: "ladder-angle",
    icon: Ruler,
    title: "Working at heights",
    lead: "One check, two steps, because a ladder is two different kinds of fact. The phone laid flat against the rail reads the setup angle from its own inclinometer - one hand, in the dark, with no signal. Then it photographs the set-up.",
    detail: [
      `OSHA 1926.1053 guideline's 4:1 rule puts a straight ladder at ${NFR_TARGETS.ladderTargetAngleDeg}°. TaskIQ reads it to about ${NFR_TARGETS.ladderAngleToleranceDeg}°, which is well inside the tolerance the rule itself carries - and the standard is named on the record a reviewer reads. Leave the phone still for five seconds and the reading takes itself, because a technician reaching for the screen has already moved the thing being measured.`,
      "The angle is a measurement and carries no confidence: a phone against a rail either read 74.8° or it did not. Everything else a ladder has to satisfy - feet level on the ground, tied off at the top, rails and rungs undamaged, three feet above the landing - is something you can see in a photograph and nothing an inertial sensor will ever answer, so it goes to the model instead, one row at a time, each with the reason it gave.",
      "Ground firmness is in neither. Nothing optical measures soil bearing capacity, so the detector is never asked about it - it arrives as a plain switch after the rows, in the technician's own words and marked as theirs.",
    ],
    targets: [
      `Target ${NFR_TARGETS.ladderTargetAngleDeg}° ± ${NFR_TARGETS.ladderAngleToleranceDeg}°`,
      "The reading runs entirely on the device",
    ],
  },
  {
    id: "ppe",
    icon: ShieldCheck,
    title: "PPE check",
    lead: "Front camera, at arm's length, before the climb. Hard hat, safety glasses, gloves, hi-vis, boots, harness and lanyard, read off the photograph and recorded against the appointment.",
    detail: [
      "Equipment detection only. TaskIQ never performs facial recognition, and the model has no concept of who is in the frame - only of what they are wearing.",
      "Every row comes back as one of three answers, and the third one is the one that matters: found, not there, or not in shot. “We did not look” and “it was not there” are different claims, and a checklist that only has a tick box has to lie about one of them. An arm's-length selfie usually stops above the knee, so boots are genuinely often the third answer.",
      "Each row arrives pre-filled and none of them is final. The technician's tick is what goes on the record; where the two disagree, the record says so and keeps both.",
    ],
    targets: ["Three answers per item, never two", "No facial recognition"],
  },
  {
    id: "offline",
    icon: CloudOff,
    title: "Works with no signal",
    lead: "Capture and the on-device checks never touch the network. The assessment is written locally, queued, and synced when coverage returns.",
    detail: [
      "The technician always knows which state they are in - “saved, will sync” is a normal outcome and never reads as a failure.",
      "Each record carries a client-generated identifier that doubles as an idempotency key, so a retry after a lost response cannot produce a duplicate assessment.",
    ],
    targets: ["Assessment never lost", "No duplicate on retry"],
  },
  {
    id: "record",
    icon: FileCheck2,
    title: "The record",
    lead: "Timestamped, geotagged, and attributable to the person who signed it. This is the part the office bought.",
    detail: [
      "Findings, tiers, acknowledgements, the angle reading, every checklist row with what read it and how sure it was, and the media it was read from - against a site and an appointment.",
      "Standalone by default. Webhooks, a REST API, scheduled exports and a BI feed are an optional layer on top - connect one system, several, or none.",
    ],
    targets: ["Written at capture, not at sync", "Export or integrate, or neither"],
  },
];

export function Product() {
  return (
    <>
      <PageHero
        eyebrow="How it works"
        title="Four checks, one record, ninety seconds."
        lead="The sequence a technician runs before they start, in the order they would do it anyway. Everything below happens on the phone in their hand."
        aside={
          <Reveal delay={80}>
            <Card tone="panel" padding="md">
              <p className="eyebrow eyebrow--sm eyebrow--panel-subtle">Where the time goes</p>
              <SecondScale tone="panel" className="product__scale" />
              <ul className="phase-list">
                {PHASES.map((phase) => (
                  <li key={phase.id} className="phase-list__item">
                    <span className="phase-list__label">{phase.label}</span>
                    <span className="phase-list__value">{phase.to - phase.from}s</span>
                  </li>
                ))}
              </ul>
            </Card>
          </Reveal>
        }
      />

      {/* ── The sequence ──────────────────────────────────────────────────── */}
      <Section>
        <ol className="step-list">
          {STEPS.map((step, index) => (
            <Reveal as="li" key={step.id}>
              <div id={step.id} className="step">
                <div className="step__grid">
                  <div className="step__marks">
                    <span aria-hidden className="step-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <IconTile size="xl">
                      <step.icon />
                    </IconTile>
                  </div>

                  <div>
                    <h2 className="step__title">{step.title}</h2>
                    <p className="prose prose--lg step__lead">{step.lead}</p>
                    <IconList
                      tone="ok"
                      className="step__targets"
                      items={step.targets.map((target) => ({ icon: CheckCircle2, text: target }))}
                    />
                  </div>

                  <div className="step__detail">
                    {step.detail.map((paragraph) => (
                      <p key={paragraph} className="prose">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </ol>
      </Section>

      {/* ── The console ───────────────────────────────────────────────────── */}
      <Section tone="sunken">
        <SectionIntro
          eyebrow="The other half"
          title="A console for the people who have to answer for it"
          lead="Sites, crews and assessments, scoped so each person sees exactly what is theirs. Access is enforced in the database rather than in the interface, which is a longer story and a better one."
        />

        <div className="console-grid">
          {[
            {
              t: "Sites and crews",
              d: "Group technicians by depot, region or client. Every assessment is filed against one.",
            },
            {
              t: "Invitations, not accounts",
              d: "Invite a technician by email; they set their own password. Cancelling an invitation and deactivating an employee are the same action, because they were never two things.",
            },
            {
              t: "Reach, not roles",
              d: "A field officer sees their assigned sites, an admin sees their company. Removing a filter from a query returns fewer rows, never someone else's.",
            },
          ].map(({ t, d }, index) => (
            <Reveal key={t} delay={index * 60}>
              <Card padding="md" fill>
                <div>
                  <h3 className="blurb__title">{t}</h3>
                  <p className="blurb__body">{d}</p>
                </div>
              </Card>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ── Honest limits ─────────────────────────────────────────────────── */}
      {/* <Section id="limits">
        <SectionIntro
          eyebrow="Honest limits"
          title="What we don't claim"
          lead="Over-promising on physics is how a safety product loses the crew that has to use it. If a technician catches the app being confidently wrong once, they stop reading it - and then the tool is worse than nothing."
        />

        <dl className="limits-grid">
          {[
            {
              t: "Ground firmness",
              d: "No camera measures soil bearing capacity. This stays a human sign-off in the checklist, and it is labelled as one rather than quietly inferred.",
            },
            {
              t: "Wiring inside walls",
              d: "Nothing optical sees through drywall. We flag the visible outlets and panels near a drill point and prompt you to think about the runs between them - an advisory, not a detector.",
            },
            {
              t: "Ladder length",
              d: "A single lens cannot measure absolute distance reliably. Depth-sensor phones can estimate it, and that will only ever be guidance - never a measurement of record.",
            },
          ].map(({ t, d }, index) => (
            <Reveal key={t} delay={index * 60}>
              <div aria-hidden className="heading-mark limits-grid__mark" />
              <dt className="blurb__title blurb__title--lg">{t}</dt>
              <dd className="blurb__body blurb__body--lg">{d}</dd>
            </Reveal>
          ))}
        </dl>
      </Section> */}

      <CtaBand />
    </>
  );
}
